import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { Box } from '@mui/material';
import { DetailView } from '@pro4-erp/core-web';
import { useCreatePageStore } from './useCreatePageStore';
import { getMessageFromError } from '@/core';
import Form from '../../components/Form';
import CompetencesCreate from './CompetencesCreate';
import { createAuditorProposal } from '../../api/createAuditorProposal';

const CreatePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const createPageStore = useCreatePageStore();

  const { mutate } = useMutation({
    mutationFn: createAuditorProposal,
    onSuccess: ({ id }) => {
      enqueueSnackbar(
        t('Successfully created new Auditor proposal.', {
          defaultValue: 'Successfully created new Auditor proposal.',
        }),
        { variant: 'success' }
      );
      queryClient.invalidateQueries(['auditor-proposals']);
      navigate(`/auditor-proposals/`);
    },
    onError: (error: Error) => {
      enqueueSnackbar(getMessageFromError(error), { variant: 'error' });
    },
  });

  useEffect(() => {
    createPageStore.reset();
    return () => {
      createPageStore.reset();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box sx={{ mt: 1 }}>
      <DetailView
        title={
          <DetailView.Title>
            {t('Create new Auditor proposal', {
              defaultValue: 'Create new Auditor proposal',
            }).toString()}
          </DetailView.Title>
        }
        onGoBack={() => navigate('/auditor-proposals')}
        tabs={[
          {
            id: 'details',
            title: t('Details', { defaultValue: 'Details' }),
          },
          {
            id: 'competences',
            title: t('Competences', { defaultValue: 'Competences' }),
          },
        ]}
        defaultTab="details"
        childrenSx={{ padding: '2rem' }}
      >
        {({ TabContent }) => (
          <>
            <TabContent tab="details">
              <Form
                isFormEditable
                onSubmit={(data) => {
                  mutate({
                    data: {
                      ...data,
                      auditorCompetenceIds: createPageStore.competences.map(
                        (competence) => competence.id
                      ),
                    },
                  });
                }}
                initialState={createPageStore.form}
                onUnmount={(form) => createPageStore.setForm({ ...form })}
              />
            </TabContent>
            <TabContent tab="competences">
              <CompetencesCreate />
            </TabContent>
          </>
        )}
      </DetailView>
    </Box>
  );
};

export default CreatePage;
